// src/pages/SessionData.js
import React, { useState, useEffect, useCallback } from 'react';
import { doc, setDoc, getDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import ConfirmationDialog from '../components/ConfirmationDialog';
import TrackMap from '../components/TrackMap';
import Stopwatch from '../components/Stopwatch';
import { updateLastModified } from '../utils/backup';
import { isEncryptionEnabled, getStoredPassphrase, decryptData, storePassphrase } from '../utils/encryption';
import PassphrasePrompt from '../components/PassphrasePrompt';
import { OPENWEATHER_API_KEY } from '../config';
import '../styles.css';

function SessionData() {
  const [sessionName, setSessionName] = useState('');
  const [comment, setComment] = useState('');
  const [time, setTime] = useState('');
  const [temperatureC, setTemperatureC] = useState('');
  const [temperatureF, setTemperatureF] = useState('');
  const [trackTempC, setTrackTempC] = useState('');
  const [trackTempF, setTrackTempF] = useState('');
  const [humidity, setHumidity] = useState('');
  const [windKmh, setWindKmh] = useState('');
  const [windMph, setWindMph] = useState('');
  const [windDir, setWindDir] = useState('');
  const [bestLap, setBestLap] = useState('');
  const [bestLapIndex, setBestLapIndex] = useState(null);
  const [numLaps, setNumLaps] = useState('');
  const [lapTimes, setLapTimes] = useState([]);
  const [newLapTime, setNewLapTime] = useState('');
  const [balanceNotes, setBalanceNotes] = useState('');
  const [vehicleId, setVehicleId] = useState(null);
  const [eventId, setEventId] = useState(null);
  // Vehicle tabs for multi-vehicle (grouped) sessions.
  const [groupVehicles, setGroupVehicles] = useState([]); // [{ vehicleId, name, sessionId | null }]
  const [sessionGroupId, setSessionGroupId] = useState(null);
  const [vehicleName, setVehicleName] = useState('');
  const [syncStatus, setSyncStatus] = useState('syncing');
  const [initialData, setInitialData] = useState(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSessionDeleteConfirm, setShowSessionDeleteConfirm] = useState(false);
  const [lapToDelete, setLapToDelete] = useState(null);
  const { sessionId } = useParams();
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const location = useLocation();
  // For shared sessions, read/write from the owner's account.
  const ownerUid = location.state?.ownerUid || null;
  const dataUid = ownerUid || (user ? user.uid : null);
  const [showTrackMap, setShowTrackMap] = useState(false);
  const [showPassphrasePrompt, setShowPassphrasePrompt] = useState(false);
  const [showStopwatch, setShowStopwatch] = useState(false);
  const [showLapGenerator, setShowLapGenerator] = useState(false);
  const [generateLapCount, setGenerateLapCount] = useState('');
  const [generateAverageTime, setGenerateAverageTime] = useState('');
  const [isWeatherLoading, setIsWeatherLoading] = useState(false);
  const [weatherCity, setWeatherCity] = useState('');
  const [showWeatherSuccess, setShowWeatherSuccess] = useState(false);

  // Conversion functions
  const celsiusToFahrenheit = (c) => {
    const celsius = parseFloat(c);
    if (isNaN(celsius)) return '';
    return ((celsius * 9) / 5 + 32).toFixed(1);
  };

  const fahrenheitToCelsius = (f) => {
    const fahrenheit = parseFloat(f);
    if (isNaN(fahrenheit)) return '';
    return (((fahrenheit - 32) * 5) / 9).toFixed(1);
  };

  const kmhToMph = (kmh) => {
    const kmhValue = parseFloat(kmh);
    if (isNaN(kmhValue)) return '';
    return (kmhValue * 0.621371).toFixed(1);
  };

  const mphToKmh = (mph) => {
    const mphValue = parseFloat(mph);
    if (isNaN(mphValue)) return '';
    return (mphValue / 0.621371).toFixed(1);
  };

  // Convert wind direction degrees to cardinal direction
  const degreesToDirection = (degrees) => {
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  };

  // Handle temperature changes
  const handleTemperatureCChange = (e) => {
    const value = e.target.value;
    setTemperatureC(value);
    setTemperatureF(celsiusToFahrenheit(value));
  };

  const handleTemperatureFChange = (e) => {
    const value = e.target.value;
    setTemperatureF(value);
    setTemperatureC(fahrenheitToCelsius(value));
  };

  const handleTrackTempCChange = (e) => {
    const value = e.target.value;
    setTrackTempC(value);
    setTrackTempF(celsiusToFahrenheit(value));
  };

  const handleTrackTempFChange = (e) => {
    const value = e.target.value;
    setTrackTempF(value);
    setTrackTempC(fahrenheitToCelsius(value));
  };

  // Handle wind speed changes
  const handleWindKmhChange = (e) => {
    const value = e.target.value;
    setWindKmh(value);
    setWindMph(kmhToMph(value));
  };

  const handleWindMphChange = (e) => {
    const value = e.target.value;
    setWindMph(value);
    setWindKmh(mphToKmh(value));
  };

  // Fetch current weather based on user's location
  const fetchWeather = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }

    setIsWeatherLoading(true);
    navigator.geolocation.getCurrentPosition(async (position) => {
      const { latitude, longitude } = position.coords;
      try {
        const response = await fetch(`https://api.openweathermap.org/data/2.5/weather?lat=${latitude}&lon=${longitude}&appid=${OPENWEATHER_API_KEY}&units=metric`);
        const data = await response.json();
        if (data.main && data.wind) {
          const tempC = data.main.temp.toFixed(1);
          setTemperatureC(tempC);
          setTemperatureF(celsiusToFahrenheit(tempC));
          setHumidity(data.main.humidity.toString());
          const windKmhValue = (data.wind.speed * 3.6).toFixed(1); // units=metric gives m/s
          setWindKmh(windKmhValue);
          setWindMph(kmhToMph(windKmhValue));
          setWindDir(degreesToDirection(data.wind.deg));

          // Show success dialog with city and state/province
          const locationParts = [];
          if (data.name) locationParts.push(data.name);
          if (data.state) locationParts.push(data.state);
          setWeatherCity(locationParts.join(', ') || 'Unknown Location');
          setShowWeatherSuccess(true);
        } else {
          alert('Weather data not available.');
        }
      } catch (error) {
        console.error('Error fetching weather:', error);
        alert('Failed to fetch weather data.');
      } finally {
        setIsWeatherLoading(false);
      }
    }, (error) => {
      console.error('Error getting location:', error);
      alert('Failed to get location.');
      setIsWeatherLoading(false);
    });
  };

  // Format time by inserting : and . (e.g., 123456 to 1:23.456)
  const formatLapTime = (input) => {
    if (!input || typeof input !== 'string') return '';
    const cleaned = input.replace(/[^0-9]/g, '');
    if (cleaned.length < 6) return cleaned;
    const minutes = cleaned.slice(0, -5);
    const seconds = cleaned.slice(-5, -3);
    const milliseconds = cleaned.slice(-3);
    return `${minutes || '0'}:${seconds.padStart(2, '0')}.${milliseconds.padStart(3, '0')}`;
  };

  // Format milliseconds to lap time format (e.g., 83456ms to 1:23.456)
  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const ms = Math.floor((milliseconds % 1000) / 10);
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  };

  // Parse formatted time to number for comparison (e.g., 1:23.456 to 123456)
  const parseLapTime = (formatted) => {
    if (!formatted || !formatted.includes(':')) return Infinity;
    const cleaned = formatted.replace(/[^0-9]/g, '');
    return parseInt(cleaned) || Infinity;
  };

  // Format time difference as +S.SSS
  const formatTimeDifference = (current, best) => {
    if (!current || !best || current === best) return '';
    const diff = parseLapTime(current) - parseLapTime(best);
    if (diff === Infinity) return '';
    const seconds = Math.floor(diff / 1000);
    const milliseconds = diff % 1000;
    return `+${seconds}.${milliseconds.toString().padStart(3, '0')}`;
  };

  // Update best lap time and index based on lap times
  const updateBestLap = useCallback((laps) => {
    if (laps.length === 0) {
      setBestLap('');
      setBestLapIndex(null);
      return;
    }
    const best = laps.reduce(
      (min, lap, index) => {
        const current = parseLapTime(lap);
        return current < parseLapTime(min.lap) ? { lap, index } : min;
      },
      { lap: laps[0], index: 0 }
    );
    setBestLap(best.lap);
    setBestLapIndex(best.index);
  }, []);

  useEffect(() => {
    if (!user || !sessionId) {
      setSyncStatus('Error: Missing user or session');
      return;
    }

    const loadData = async () => {
      setSyncStatus('syncing');
      try {
        const sessionDoc = await getDoc(doc(db, `users/${dataUid}/sessions/${sessionId}`));
        if (sessionDoc.exists()) {
          let data = sessionDoc.data();

          // Decrypt if encrypted
          if (data.encrypted) {
            const passphrase = getStoredPassphrase();
            if (!passphrase) {
              // Need to prompt for passphrase
              setShowPassphrasePrompt(true);
              return;
            }
            try {
              const decryptedData = decryptData(data.data, passphrase);
              // Handle both old and new formats
              if (decryptedData.vehicleId && decryptedData.eventId) {
                // Old format: entire session was encrypted
                data = decryptedData;
              } else {
                // New format: merge with plain metadata
                data = {
                  ...data,
                  ...decryptedData
                };
              }
              delete data.data; // Remove the encrypted blob
              delete data.encrypted; // Remove the encryption flag
            } catch (error) {
              alert('Invalid passphrase. Please check your encryption settings.');
              navigate('/settings');
              return;
            }
          }
          setSessionName(data.sessionName || '');
          setComment(data.comment || '');
          setTime(data.time || '');
          setTemperatureC(data.temperature || '');
          setTemperatureF(celsiusToFahrenheit(data.temperature || ''));
          setTrackTempC(data.trackTemp || '');
          setTrackTempF(celsiusToFahrenheit(data.trackTemp || ''));
          setHumidity(data.humidity || '');
          setWindKmh(data.wind || '');
          setWindMph(kmhToMph(data.wind || ''));
          setWindDir(data.windDir || '');
          setBestLap(data.bestLap || '');
          setNumLaps(data.numLaps || '');
          const loadedLapTimes = data.lapTimes ? data.lapTimes.split(',').filter(t => t) : [];
          setLapTimes(loadedLapTimes);
          setBalanceNotes(data.balanceNotes || '');
          setVehicleId(data.vehicleId);
          setEventId(data.eventId);
          setInitialData(JSON.stringify({
            sessionName: data.sessionName || '',
            comment: data.comment || '',
            time: data.time || '',
            temperature: data.temperature || '',
            trackTemp: data.trackTemp || '',
            humidity: data.humidity || '',
            wind: data.wind || '',
            windDir: data.windDir || '',
            bestLap: data.bestLap || '',
            numLaps: data.numLaps || '',
            lapTimes: data.lapTimes || '',
            balanceNotes: data.balanceNotes || ''
          }));
          updateBestLap(loadedLapTimes);
          // Resolve the current vehicle's name for the heading pill.
          try {
            const vDoc = await getDoc(doc(db, `users/${dataUid}/vehicles/${data.vehicleId}`));
            if (vDoc.exists()) setVehicleName(vDoc.data().name || 'Unnamed Vehicle');
          } catch (e) { /* non-fatal */ }
          loadGroupVehicles(data.sessionGroupId, data.sessionVehicleIds, data.vehicleId);
          setSyncStatus(navigator.onLine ? 'synced' : 'offline');
        } else {
          console.error('Session not found:', sessionId);
          setSyncStatus('Error: Session not found');
        }
      } catch (err) {
        console.error('Session fetch error:', err.message);
        setSyncStatus(`Error: ${err.message}`);
      }
    };

    // Build the vehicle tab list for a grouped (multi-vehicle) session.
    const loadGroupVehicles = async (groupId, roster, ownVehicleId) => {
      setSessionGroupId(groupId || null);
      if (!groupId || !roster || roster.length <= 1) {
        setGroupVehicles([]);
        return;
      }
      try {
        // Map vehicleId -> existing sibling session doc id.
        const siblingsSnap = await getDocs(query(
          collection(db, `users/${dataUid}/sessions`),
          where('sessionGroupId', '==', groupId)
        ));
        const sessionIdByVehicle = {};
        siblingsSnap.docs.forEach(d => { sessionIdByVehicle[d.data().vehicleId] = d.id; });
        // Resolve vehicle names.
        const vehiclesSnap = await getDocs(collection(db, `users/${dataUid}/vehicles`));
        const nameById = {};
        vehiclesSnap.docs.forEach(d => { nameById[d.id] = d.data().name || 'Unnamed Vehicle'; });
        setGroupVehicles(roster.map(vid => ({
          vehicleId: vid,
          name: nameById[vid] || 'Unnamed Vehicle',
          sessionId: sessionIdByVehicle[vid] || null,
        })));
      } catch (err) {
        console.error('Group vehicles load error:', err.message);
        setGroupVehicles([]);
      }
    };

    if (!loading && user) loadData();
    if (loading) {
      setSyncStatus('syncing');
      return;
    }

    // Update sync status on connectivity
    const updateSyncStatus = () => {
      setSyncStatus(navigator.onLine ? 'synced' : 'offline');
    };
    window.addEventListener('online', updateSyncStatus);
    window.addEventListener('offline', updateSyncStatus);
    return () => {
      window.removeEventListener('online', updateSyncStatus);
      window.removeEventListener('offline', updateSyncStatus);
    };
  }, [user, loading, sessionId, updateBestLap]);

  // Persist the current vehicle's session, syncing shared fields to siblings.
  // Returns true on success. Does not navigate.
  const persistData = async () => {
    if (!user || !sessionId || !eventId) {
      setSyncStatus('Error: Missing data');
      alert('Cannot save: Missing session or event data');
      return false;
    }
    const sessionData = {
      sessionName,
      comment,
      time,
      temperature: temperatureC, // Store in Celsius
      trackTemp: trackTempC, // Store in Celsius
      humidity,
      wind: windKmh, // Store in km/h
      windDir,
      bestLap,
      numLaps,
      lapTimes: lapTimes.join(','),
      balanceNotes,
      timestamp: Date.now(),
      vehicleId: Number(vehicleId),
      eventId,
      // Edit attribution (notifies the owner when a shared user makes changes)
      lastEditedBy: user?.uid,
      lastEditedByEmail: user?.email || '',
      lastEditedAt: Date.now(),
    };
    try {
      setSyncStatus('syncing');
      await setDoc(doc(db, `users/${dataUid}/sessions/${sessionId}`), sessionData, { merge: true });
      // Propagate shared fields (weather, session name & time) to sibling vehicle docs.
      if (sessionGroupId && groupVehicles.length > 1) {
        try {
          const siblingsSnap = await getDocs(query(
            collection(db, `users/${dataUid}/sessions`),
            where('sessionGroupId', '==', sessionGroupId)
          ));
          const sharedFields = {
            sessionName,
            time,
            temperature: temperatureC,
            trackTemp: trackTempC,
            humidity,
            wind: windKmh,
            windDir,
          };
          await Promise.all(
            siblingsSnap.docs
              .filter(d => d.id !== sessionId)
              .map(d => setDoc(d.ref, sharedFields, { merge: true }))
          );
        } catch (syncErr) {
          console.error('Group sync error:', syncErr.message);
        }
      }
      await updateLastModified(user);
      setInitialData(JSON.stringify(sessionData));
      setSyncStatus('synced');
      return true;
    } catch (err) {
      console.error('Save session error:', err.message);
      setSyncStatus(`Error: ${err.message}`);
      alert('Failed to save session: ' + err.message);
      return false;
    }
  };

  const saveData = async () => {
    if (await persistData()) {
      navigate(`/event/${eventId}`, { state: { vehicleId, ownerUid } });
    }
  };

  const deleteSession = async () => {
    setShowSessionDeleteConfirm(true);
  };

  const confirmDeleteSession = async () => {
    if (!user || !sessionId || !eventId) {
      setSyncStatus('Error: Missing data');
      return;
    }
    try {
      await deleteDoc(doc(db, `users/${dataUid}/sessions/${sessionId}`));
      setSyncStatus('Synced');
      setShowSessionDeleteConfirm(false);
      navigate(`/event/${eventId}`, { state: { vehicleId, ownerUid } });
    } catch (err) {
      console.error('Delete session error:', err.message);
      setSyncStatus(`Error: ${err.message}`);
      alert('Failed to delete session: ' + err.message);
    }
  };

  const switchVehicleTab = async (tab) => {
    if (String(tab.vehicleId) === String(vehicleId)) return;
    // Save the current car's data (and sync shared fields) before switching,
    // so nothing entered is lost when we navigate to the sibling doc.
    const saved = await persistData();
    if (!saved) return;
    if (tab.sessionId) {
      navigate(`/session-data/${tab.sessionId}`, { state: { vehicleId: tab.vehicleId, eventId, ownerUid } });
    } else {
      // No saved data for this car yet — go to its setup sheet within the group.
      navigate('/session-setup', {
        state: {
          vehicleId: tab.vehicleId,
          eventId,
          ownerUid,
          sessionGroupId,
          sessionVehicleIds: groupVehicles.map(v => v.vehicleId),
          sessionName,
          time,
        },
      });
    }
  };

  const handleClose = () => {
    const currentData = {
      sessionName,
      comment,
      time,
      temperature: temperatureC,
      trackTemp: trackTempC,
      humidity,
      wind: windKmh,
      windDir,
      bestLap,
      numLaps,
      lapTimes: lapTimes.join(','),
      balanceNotes
    };
    if (JSON.stringify(currentData) !== initialData) {
      setShowDiscardConfirm(true);
    } else {
      navigate(`/event/${eventId}`, { state: { vehicleId, ownerUid } });
    }
  };

  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    navigate(`/event/${eventId}`, { state: { vehicleId, ownerUid } });
  };

  const handleAddLapTime = () => {
    if (newLapTime) {
      const formattedTime = formatLapTime(newLapTime);
      if (formattedTime.includes(':')) {
        const updatedLapTimes = [...lapTimes, formattedTime];
        setLapTimes(updatedLapTimes);
        setNewLapTime('');
        setNumLaps(updatedLapTimes.length.toString());
        updateBestLap(updatedLapTimes);
      }
    }
  };

  const handleDeleteLapTime = (index) => {
    setLapToDelete(index);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteLap = () => {
    const updatedLapTimes = lapTimes.filter((_, i) => i !== lapToDelete);
    setLapTimes(updatedLapTimes);
    setNumLaps(updatedLapTimes.length.toString());
    updateBestLap(updatedLapTimes);
    setShowDeleteConfirm(false);
    setLapToDelete(null);
  };

  const handleAddStopwatchLaps = (newLaps) => {
    const updatedLapTimes = [...lapTimes, ...newLaps];
    setLapTimes(updatedLapTimes);
    setNumLaps(updatedLapTimes.length.toString());
    updateBestLap(updatedLapTimes);
  };

  const generateLaps = () => {
    const count = parseInt(generateLapCount);
    const lapTimeInput = generateAverageTime;

    if (!count || count <= 0 || !lapTimeInput) {
      alert('Please enter valid number of laps and lap time');
      return;
    }

    // Format the entered time
    const formattedLapTime = formatLapTime(lapTimeInput);
    if (!formattedLapTime.includes(':')) {
      alert('Please enter a valid lap time format (M:SS.SSS)');
      return;
    }

    // Generate laps - all the same time
    const generatedLaps = [];
    for (let i = 0; i < count; i++) {
      // Mark as generated lap
      generatedLaps.push(`${formattedLapTime}*`);
    }

    const updatedLapTimes = [...lapTimes, ...generatedLaps];
    setLapTimes(updatedLapTimes);
    setNumLaps(updatedLapTimes.length.toString());
    updateBestLap(updatedLapTimes);

    // Reset form and close dialog
    setGenerateLapCount('');
    setGenerateAverageTime('');
    setShowLapGenerator(false);
  };

  const handleNewLapTimeChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setNewLapTime(value);
  };

  const handleGenerateTimeChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setGenerateAverageTime(value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddLapTime();
    }
  };

  return (
    <div className="session-data-container">
      <Header syncStatus={syncStatus} />
      <div className="page-title">
        <h1>Session Data{vehicleName && <span className="session-info-vehicle">{vehicleName}</span>}</h1>
        <button onClick={handleClose} className="close-button">✕</button>
      </div>
      <div className="session-data-details">
        <div className="session-data-fields">
          <span className="session-data-label">Session Name:</span>
          <input
            value={sessionName}
            onChange={e => setSessionName(e.target.value)}
            placeholder="Session Name"
            className="session-data-input"
          />
        </div>
        <input
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Comment"
          className="session-data-fullwidth"
        />
        <div className="session-data-fields">
          <span className="session-data-label">Date & Time:</span>
          <input
            type="datetime-local"
            value={time}
            onChange={e => setTime(e.target.value)}
            className="session-data-input"
          />
        </div>
        <div className="session-data-fields dual-input-param">
          <div className="session-data-label-div"><span className="session-data-label">Temperature:</span></div>
          <div className="dual-input-container">
            <button onClick={fetchWeather} className="weather-button" title="Fetch current weather" disabled={isWeatherLoading}>
              {isWeatherLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                </div>
              ) : (
                '🌤️'
              )}
            </button>
            <input
              value={temperatureC}
              onChange={handleTemperatureCChange}
              placeholder="Celsius"
              className="session-data-input session-data-input-aligned"
              type="number"
              step="0.1"
            />
            <span className="unit-label">°C</span>
            <input
              value={temperatureF}
              onChange={handleTemperatureFChange}
              placeholder="Fahrenheit"
              className="session-data-input session-data-input-aligned"
              type="number"
              step="0.1"
            />
            <span className="unit-label">°F</span>
          </div>
        </div>
        <div className="session-data-fields dual-input-param">
          <div className="session-data-label-div"><span className="session-data-label">Track Temperature:</span></div>
          <div className="dual-input-container">
            <input
              value={trackTempC}
              onChange={handleTrackTempCChange}
              placeholder="Celsius"
              className="session-data-input session-data-input-aligned"
              type="number"
              step="0.1"
            />
            <span className="unit-label">°C</span>
            <input
              value={trackTempF}
              onChange={handleTrackTempFChange}
              placeholder="Fahrenheit"
              className="session-data-input session-data-input-aligned"
              type="number"
              step="0.1"
            />
            <span className="unit-label">°F</span>
          </div>
        </div>
        <div className="session-data-fields">
          <span className="session-data-label">Humidity:</span>
          <input
            value={humidity}
            onChange={e => setHumidity(e.target.value)}
            placeholder="Humidity"
            className="session-data-input"
          />
        </div>
        <div className="session-data-fields dual-input-param">
          <div className="session-data-label-div"><span className="session-data-label">Wind:</span></div>
          <div className="dual-input-container">
            <input
              value={windKmh}
              onChange={handleWindKmhChange}
              placeholder="km/h"
              className="session-data-input session-data-input-aligned"
              type="number"
              step="0.1"
            />
            <span className="unit-label">km/h</span>
            <input
              value={windMph}
              onChange={handleWindMphChange}
              placeholder="mph"
              className="session-data-input session-data-input-aligned"
              type="number"
              step="0.1"
            />
            <span className="unit-label">mph</span>
          </div>
        </div>
        <div className="session-data-fields">
          <span className="session-data-label">Wind Direction:</span>
          <input
            value={windDir}
            onChange={e => setWindDir(e.target.value)}
            placeholder="Wind Direction"
            className="session-data-input"
          />
        </div>
        {groupVehicles.length > 1 && (
          <div className="vehicle-tabs-section">
            <div className="vehicle-tabs-heading">Select Vehicle</div>
            <div className="vehicle-tabs">
              {groupVehicles.map(tab => (
                <button
                  key={tab.vehicleId}
                  className={`vehicle-tab ${String(tab.vehicleId) === String(vehicleId) ? 'active' : ''} ${tab.sessionId ? '' : 'vehicle-tab-empty'}`}
                  onClick={() => switchVehicleTab(tab)}
                >
                  {tab.name}
                  {!tab.sessionId && <span className="vehicle-tab-badge">no data</span>}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="session-data-fields">
          <span className="session-data-label">Number of Laps:</span>
          <div className="input-with-button">
            <button onClick={() => setShowLapGenerator(true)} className="btn btn-group-action edit weather-button" title="Generate Laps">✏️</button>
            <input
              value={numLaps}
              onChange={e => setNumLaps(e.target.value)}
              placeholder="Number of Laps"
              className="session-data-input"
              readOnly
            />
          </div>
        </div>
        <div className="lap-times-container">
          <div className="lap-time-input-group">
            <input
              value={formatLapTime(newLapTime)}
              onChange={handleNewLapTimeChange}
              onKeyPress={handleKeyPress}
              placeholder="Lap Time (M:SS.SSS)"
              className="session-data-input"
            />
            <button onClick={handleAddLapTime} className="btn btn-group-action add">+</button>
            <button onClick={() => setShowStopwatch(true)} className="btn btn-group-action stopwatch">⏱️</button>
          </div>
          <div className="lap-times-list">
            {lapTimes.map((lap, index) => (
              <div key={index} className="lap-time-item">
                <span>
                  Lap {index + 1}: {lap}
                  {lap === bestLap ? (
                    <span className="lap-time-difference"> (Best lap)</span>
                  ) : (
                    <span className="lap-time-difference"> {formatTimeDifference(lap, bestLap)}</span>
                  )}
                </span>
                <button onClick={() => handleDeleteLapTime(index)} className="btn btn-group-action delete">-</button>
              </div>
            ))}
          </div>
          <div className="best-lap-display">
            <span>Best Lap: {bestLap || 'N/A'}</span>
            {bestLapIndex !== null && <span className="best-lap-index"> (Lap {bestLapIndex + 1})</span>}
          </div>
        </div>
        <button onClick={() => setShowTrackMap(true)} className="btn btn-save">Open Track Map</button>
        <div className="session-data-fields">
          <textarea
            value={balanceNotes}
            onChange={e => setBalanceNotes(e.target.value)}
            placeholder="Notes"
            className="tab-notes"
          />
        </div>
        <div className="session-buttons">
          <button onClick={saveData} className="btn btn-save">Save</button>
          {!ownerUid && <button onClick={deleteSession} className="btn btn-danger">Delete Session</button>}
        </div>
      </div>
      {showDiscardConfirm && (
        <ConfirmationDialog
          message="Are you sure you want to discard unsaved changes?"
          onConfirm={confirmDiscard}
          onCancel={() => setShowDiscardConfirm(false)}
        />
      )}
      {showDeleteConfirm && (
        <ConfirmationDialog
          message={`Are you sure you want to delete Lap ${lapToDelete + 1}?`}
          onConfirm={confirmDeleteLap}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
      {showSessionDeleteConfirm && (
        <ConfirmationDialog
          message="Are you sure you want to delete this session?"
          onConfirm={confirmDeleteSession}
          onCancel={() => setShowSessionDeleteConfirm(false)}
        />
      )}
      {showTrackMap && <TrackMap sessionId={sessionId} onClose={() => setShowTrackMap(false)} />}
      <Stopwatch
        isOpen={showStopwatch}
        onClose={() => setShowStopwatch(false)}
        currentLapCount={lapTimes.length}
        onAddLaps={handleAddStopwatchLaps}
        existingLaps={lapTimes}
      />

      {showLapGenerator && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Generate Laps</h2>
              <button onClick={() => setShowLapGenerator(false)} className="close-button">✕</button>
            </div>
            <div className="modal-content">
              <div className="session-data-fields">
                <span className="session-data-label">Number of Laps:</span>
                <input
                  type="number"
                  value={generateLapCount}
                  onChange={e => setGenerateLapCount(e.target.value)}
                  placeholder="Enter number of laps"
                  className="session-data-input"
                  min="1"
                />
              </div>
              <div className="session-data-fields">
                <span className="session-data-label">Lap Time:</span>
                <input
                  value={formatLapTime(generateAverageTime)}
                  onChange={handleGenerateTimeChange}
                  placeholder="Lap Time (M:SS.SSS)"
                  className="session-data-input"
                />
              </div>
              <div className="modal-actions">
                <button onClick={() => setShowLapGenerator(false)} className="btn btn-secondary">Cancel</button>
                <button onClick={generateLaps} className="btn btn-save">Generate Laps</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showWeatherSuccess && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Weather Updated</h2>
              <button onClick={() => setShowWeatherSuccess(false)} className="close-button">✕</button>
            </div>
            <div className="modal-content">
              <p>Weather data successfully fetched for:</p>
              <p style={{ fontWeight: 'bold', marginTop: '8px' }}>{weatherCity}</p>
              <div className="modal-actions">
                <button onClick={() => setShowWeatherSuccess(false)} className="btn btn-save">OK</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPassphrasePrompt && (
        <PassphrasePrompt
          message="Enter your encryption passphrase to access this session data:"
          onSuccess={(passphrase) => {
            setShowPassphrasePrompt(false);
            // Store the passphrase for future use
            storePassphrase(passphrase);
            // Retry loading the session data
            const loadData = async () => {
              setSyncStatus('syncing');
              try {
                const sessionDoc = await getDoc(doc(db, `users/${dataUid}/sessions/${sessionId}`));
                if (sessionDoc.exists()) {
                  let data = sessionDoc.data();

                  // Decrypt if encrypted
                  if (data.encrypted) {
                    try {
                      const decryptedData = decryptData(data.data, passphrase);
                      // Handle both old and new formats
                      if (decryptedData.vehicleId && decryptedData.eventId) {
                        // Old format: entire session was encrypted
                        data = decryptedData;
                      } else {
                        // New format: merge with plain metadata
                        data = {
                          ...data,
                          ...decryptedData
                        };
                      }
                      delete data.data; // Remove the encrypted blob
                      delete data.encrypted; // Remove the encryption flag
                    } catch (error) {
                      alert('Invalid passphrase. Please check your encryption settings.');
                      navigate('/settings');
                      return;
                    }
                  }

                  // Continue with setting the data...
                  setSessionName(data.sessionName || '');
                  setComment(data.comment || '');
                  setTime(data.time || '');
                  setTemperatureC(data.temperature || '');
                  setTemperatureF(celsiusToFahrenheit(data.temperature || ''));
                  setTrackTempC(data.trackTemp || '');
                  setTrackTempF(celsiusToFahrenheit(data.trackTemp || ''));
                  setHumidity(data.humidity || '');
                  setWindKmh(data.wind || '');
                  setWindMph(kmhToMph(data.wind || ''));
                  setWindDir(data.windDir || '');
                  setBestLap(data.bestLap || '');
                  setNumLaps(data.numLaps || '');
                  const loadedLapTimes = data.lapTimes ? data.lapTimes.split(',').filter(t => t) : [];
                  setLapTimes(loadedLapTimes);
                  setBalanceNotes(data.balanceNotes || '');
                  setVehicleId(data.vehicleId);
                  setEventId(data.eventId);
                  setInitialData(JSON.stringify({
                    sessionName: data.sessionName || '',
                    comment: data.comment || '',
                    time: data.time || '',
                    temperature: data.temperature || '',
                    trackTemp: data.trackTemp || '',
                    humidity: data.humidity || '',
                    wind: data.wind || '',
                    windDir: data.windDir || '',
                    bestLap: data.bestLap || '',
                    numLaps: data.numLaps || '',
                    lapTimes: data.lapTimes || '',
                    balanceNotes: data.balanceNotes || ''
                  }));
                  updateBestLap(loadedLapTimes);
                  setSyncStatus(navigator.onLine ? 'synced' : 'offline');
                } else {
                  console.error('Session not found:', sessionId);
                  setSyncStatus('Error: Session not found');
                }
              } catch (err) {
                console.error('Session fetch error:', err.message);
                setSyncStatus(`Error: ${err.message}`);
              }
            };
            loadData();
          }}
          onCancel={() => {
            setShowPassphrasePrompt(false);
            navigate('/settings');
          }}
        />
      )}
    </div>
  );
}

export default SessionData;